import React from 'react';
import PropTypes from 'prop-types';
import { X, Link, Paperclip } from 'lucide-react';

const pill = {
  background: 'rgba(0,0,0,0.05)',
  border: '1px solid rgba(0,0,0,0.10)',
  color: 'rgba(0,0,0,0.60)',
};

const AddClipModal = ({
  isOpen,
  onClose,
  addMode,
  setAddMode,
  newInterest,
  setNewInterest,
  onSubmit,
  onFileUpload,
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setAddMode(null);
    setNewInterest('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.30)' }} onClick={handleClose} />

      <div
        className="relative w-full max-w-lg"
        style={{
          backgroundColor: '#F8F5EE',
          borderRadius: '20px 20px 0 0',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.10)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full" style={{ background: 'rgba(0,0,0,0.15)' }} />
        </div>

        <div className="px-5 pt-3 pb-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase font-semibold mb-0.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                Clipboard
              </p>
              <h3 className="font-instrument text-[28px] leading-tight text-[#111]">Add Clip</h3>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)' }}
              aria-label="Close"
            >
              <X className="w-4 h-4" style={{ color: 'rgba(0,0,0,0.55)' }} />
            </button>
          </div>

          <div className="my-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.07)' }} />

          {!addMode ? (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setAddMode('link')}
                className="py-4 px-4 rounded-[12px] flex flex-col items-center gap-2 transition-all active:scale-[0.97]"
                style={pill}
              >
                <Link className="w-5 h-5" style={{ color: 'rgba(0,0,0,0.55)' }} />
                <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.55)' }}>
                  Link
                </span>
              </button>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                  onChange={(e) => { onFileUpload(e); handleClose(); }}
                  className="hidden"
                  multiple
                />
                <div
                  className="py-4 px-4 rounded-[12px] flex flex-col items-center gap-2 transition-all active:scale-[0.97]"
                  style={pill}
                >
                  <Paperclip className="w-5 h-5" style={{ color: 'rgba(0,0,0,0.55)' }} />
                  <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.55)' }}>
                    File
                  </span>
                </div>
              </label>
            </div>
          ) : (
            <form
              onSubmit={(e) => { onSubmit(e); setAddMode(null); }}
              className="space-y-3"
            >
              <input
                type="text"
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                placeholder="Paste a link…"
                className="w-full px-4 py-3 text-[15px] rounded-[12px] focus:outline-none"
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.10)',
                  color: '#111',
                }}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAddMode(null)}
                  className="flex-1 px-4 py-3 rounded-[12px] text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
                  style={{ letterSpacing: '1.5px', ...pill }}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!newInterest.trim()}
                  className="flex-1 px-4 py-3 rounded-[20px] text-[11px] uppercase font-semibold text-white bg-[#064e3b] disabled:opacity-40 transition-all active:scale-[0.97]"
                  style={{ letterSpacing: '1.5px', boxShadow: '0 3px 8px rgba(6,78,59,0.30)' }}
                >
                  Add
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

AddClipModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addMode: PropTypes.oneOf(['link', 'file', null]),
  setAddMode: PropTypes.func.isRequired,
  newInterest: PropTypes.string.isRequired,
  setNewInterest: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onFileUpload: PropTypes.func.isRequired,
};

export default AddClipModal;
